<template>
	<v-dialog
		:model-value="visible"
		@update:model-value="onDialogUpdate"
		max-width="650px"
		persistent
		class="tagging-modal-dialog"
	>
		<div class="tagging-modal" :class="colourMode" ref="modal">
			<!-- Success Tick Overlay -->
			<transition name="tick-fade">
				<div v-if="showConfirmTick" class="confirm-tick-overlay">
					<v-icon size="80" color="#4CAF50">mdi-check-circle</v-icon>
				</div>
			</transition>
			<!-- Header -->
			<div class="modal-header">
				<div class="header-left">
					<h2 class="modal-title">Tag Transaction</h2>
					<span v-if="bulkMode" class="progress-indicator">
						{{ bulkModeIndex + 1 }} of {{ bulkModeTotal }}
					</span>
				</div>
				<button class="close-btn" @click="$emit('close')">
					<v-icon>mdi-close</v-icon>
				</button>
			</div>

			<!-- Transaction Info -->
			<div class="transaction-info" v-if="localTransaction">
				<div class="transaction-info-left">
					<div class="merchant-name">{{ localTransaction.merchantName || 'Unknown Merchant' }}</div>
					<div class="transaction-date" v-if="localTransaction.timestamp">{{ formatDate(localTransaction.timestamp) }}</div>
				</div>
				<div class="transaction-info-right">
					<div class="transaction-amount">{{ formatAmount(localTransaction.amount) }}</div>
					<div v-if="merchantCount > 1" class="merchant-count">{{ merchantCount }} transactions</div>
				</div>
			</div>

			<!-- Search Bar -->
			<div class="search-section">
				<v-text-field
					v-model="searchQuery"
					prepend-inner-icon="mdi-magnify"
					density="compact"
					placeholder="Search categories..."
					variant="outlined"
					hide-details
					clearable
					single-line
				/>
			</div>

			<!-- AI Suggestion -->
			<div v-if="isUncategorized && suggestedClassification && !searchQuery" class="suggestion-banner">
				<div class="suggestion-content">
					<v-icon size="20" color="#413655">mdi-lightbulb-outline</v-icon>
					<span class="suggestion-text">
						Suggested: <strong>{{ suggestedClassification.fullName }}</strong>
					</span>
				</div>
				<button class="apply-suggestion-btn" @click="applySuggestion">Apply</button>
			</div>

			<!-- Two-Panel Category Selector -->
			<div class="category-panels">
				<!-- Left Panel: Parent Categories -->
				<div class="parent-panel">
					<div class="panel-header">Categories</div>
					<div class="parent-list">
						<button
							v-for="parent in filteredParentCategories"
							:key="parent.name"
							:class="['parent-item', { selected: selectedParent?.name === parent.name }]"
							:style="getParentStyle(parent)"
							@click="selectParent(parent)"
						>
							<span class="parent-name">{{ parent.name }}</span>
							<v-icon size="16" v-if="parent.categories.length">mdi-chevron-right</v-icon>
						</button>
					</div>
				</div>

				<!-- Right Panel: Subcategories -->
				<div class="subcategory-panel">
					<div class="panel-header">{{ selectedParent?.name || 'Subcategories' }}</div>
					<div class="subcategory-list" ref="subcategoryList" v-if="selectedParent">
						<template v-for="category in filteredSubcategories" :key="category.mgClassification.id">
							<!-- Parent category itself (if no subclassification) -->
							<button
								v-if="!category.mgClassification.mgSubClassification && category.children.length === 0"
								:class="['subcategory-item', { selected: selectedClassificationId === category.mgClassification.id }]"
								:data-id="category.mgClassification.id"
								@click="selectCategory(category)"
							>
								<span class="radio-dot" :class="{ checked: selectedClassificationId === category.mgClassification.id }"></span>
								<span class="subcategory-name">{{ category.mgClassification.fullName }}</span>
							</button>

							<!-- Parent with children - show parent as option -->
							<button
								v-if="category.children.length > 0"
								:class="['subcategory-item', { selected: selectedClassificationId === category.mgClassification.id }]"
								:data-id="category.mgClassification.id"
								@click="selectCategory(category)"
							>
								<span class="radio-dot" :class="{ checked: selectedClassificationId === category.mgClassification.id }"></span>
								<span class="subcategory-name">{{ category.mgClassification.fullName }}</span>
							</button>

							<!-- Children -->
							<button
								v-for="child in category.children"
								:key="child.mgClassification.id"
								:class="['subcategory-item child', { selected: selectedClassificationId === child.mgClassification.id }]"
								:data-id="child.mgClassification.id"
								@click="selectCategory(child)"
							>
								<span class="radio-dot" :class="{ checked: selectedClassificationId === child.mgClassification.id }"></span>
								<span class="subcategory-name">{{ child.mgClassification.mgSubClassification || child.mgClassification.fullName }}</span>
							</button>
						</template>
					</div>
					<div class="empty-state" v-else>
						Select a category from the left
					</div>
				</div>
			</div>

			<!-- Toggle Options -->
			<div class="toggle-section">
				<label class="toggle-row">
					<span class="toggle-label">Always apply to this merchant</span>
					<MGToggle v-model="alwaysApplyTag" />
				</label>
				<label class="toggle-row">
					<span class="toggle-label">Exclude from Spending Analysis</span>
					<MGToggle v-model="excludeFromAnalysis" />
				</label>
			</div>

			<!-- Tags Section -->
			<div class="tags-section" v-if="localTransaction && availableTags">
				<div class="tags-label">#Tags</div>
				<TagInput
					:selectableTags="availableTags"
					:modelValue="selectedTags"
					:maxTags="5"
					:maxTagLength="20"
					@change="handleTagsChange"
					@addedNew="handleNewTag"
				/>
			</div>

			<!-- Actions -->
			<div class="modal-actions">
				<button class="cancel-btn" @click="$emit('close')">Cancel</button>
				<button v-if="bulkMode && bulkModeIndex > 0" class="skip-btn" @click="$emit('previous')">Previous</button>
				<button v-if="bulkMode" class="skip-btn" @click="$emit('next')">Skip</button>
				<button class="confirm-btn" :disabled="!canConfirm || saving" @click="confirmTag">
					<v-progress-circular v-if="saving" indeterminate size="16" width="2" color="white" class="mr-2" />
					{{ saving ? 'Saving...' : 'Confirm' }}
				</button>
			</div>
		</div>
	</v-dialog>
</template>

<script>
import Services from '../../mixins/Services';
import { GRANDPARENT_CLASSIFICATIONS } from '../../mixins/SpendingAnalysis';
import MGToggle from '../../components/elements/MGToggle.vue';
import TagInput from '../../components/elements/TagInput.vue';

export default {
	name: 'TransactionTaggingModal',
	mixins: [Services],
	components: { MGToggle, TagInput },
	props: {
		visible: Boolean,
		transactionData: Object,
		colourMode: {
			type: String,
			default: 'light'
		},
		bulkMode: {
			type: Boolean,
			default: false
		},
		bulkModeIndex: {
			type: Number,
			default: 0
		},
		bulkModeTotal: {
			type: Number,
			default: 0
		},
		merchantCount: {
			type: Number,
			default: 1
		}
	},
	emits: ['close', 'tagged', 'next', 'previous'],
	data() {
		return {
			searchQuery: '',
			classifications: [],
			suggestedClassification: null,
			loadingSuggestion: false,
			selectedParent: null,
			selectedClassificationId: null,
			selectedClassificationObj: null,
			alwaysApplyTag: true,
			excludeFromAnalysis: false,
			parentCategories: [],
			localTransaction: null,  // Local copy to prevent prop reactivity issues
			availableTags: null,
			selectedTags: [],
			showConfirmTick: false,
			saving: false
		};
	},
	computed: {
		isUncategorized() {
			if (!this.localTransaction) return true;
			const classification = this.localTransaction.customerClassification || this.localTransaction.effectiveClassification;
			return !classification || classification.mgClassification === 'Uncategorized';
		},
		filteredParentCategories() {
			if (!this.searchQuery) return this.parentCategories;
			const q = this.searchQuery.toLowerCase();
			return this.parentCategories.filter(parent => {
				// Check if parent name matches
				if (parent.name.toLowerCase().includes(q)) return true;
				// Check if any category or child matches
				return parent.categories.some(cat =>
					cat.mgClassification.fullName.toLowerCase().includes(q) ||
					cat.children?.some(child =>
						(child.mgClassification.mgSubClassification || child.mgClassification.fullName).toLowerCase().includes(q)
					)
				);
			});
		},
		filteredSubcategories() {
			if (!this.selectedParent) return [];
			if (!this.searchQuery) return this.selectedParent.categories;
			const q = this.searchQuery.toLowerCase();
			return this.selectedParent.categories.filter(cat => {
				if (cat.mgClassification.fullName.toLowerCase().includes(q)) return true;
				return cat.children?.some(child =>
					(child.mgClassification.mgSubClassification || child.mgClassification.fullName).toLowerCase().includes(q)
				);
			});
		},
		canConfirm() {
			return this.selectedClassificationId !== null;
		}
	},
	watch: {
		visible: {
			immediate: true,
			handler(newVal) {
				if (newVal && this.transactionData) {
					this.initModal();
				} else if (!newVal) {
					this.resetState();
				}
			}
		},
		transactionData: {
			immediate: true,
			handler(newVal) {
				if (this.visible && newVal) {
					this.initModal();
				}
			}
		}
	},
	methods: {
		initModal() {
			// Store local copy when modal opens
			this.localTransaction = { ...this.transactionData };
			this.excludeFromAnalysis = this.transactionData?.excludeFromAnalysis || false;
			// Set current classification if exists
			const current = this.transactionData?.customerClassification || this.transactionData?.effectiveClassification;
			if (current?.id && current.mgClassification !== 'Uncategorized') {
				this.selectedClassificationId = current.id;
			}
			// Initialize selected tags from transaction
			if (this.transactionData.customerTags && Array.isArray(this.transactionData.customerTags)) {
				this.selectedTags = this.transactionData.customerTags.map(t => t.tag);
			} else {
				this.selectedTags = [];
			}
			this.loadData();
		},
		loadData() {
			// Load classifications
			this._getTransactionClassifications((classifications) => {
				this.classifications = classifications;
				this.buildParentCategories();

				// Select parent based on current classification
				this.selectParentFromCurrentSelection();

				// Scroll to selected item after DOM updates
				this.$nextTick(() => {
					this.scrollToSelectedSubcategory();
				});
			});

			// Load available tags
			this._getCustomerTags(() => {
				if (this.customerTags && Array.isArray(this.customerTags)) {
					this.availableTags = this.customerTags.map(t => t.tag);
				} else {
					this.availableTags = [];
				}
			});

			// Get AI suggestion if uncategorized
			if (this.isUncategorized && this.localTransaction?.merchantName) {
				this.loadingSuggestion = true;
				this._getSuggestedClassification(this.localTransaction.merchantName, (suggestion) => {
					this.suggestedClassification = suggestion;
					this.loadingSuggestion = false;
				});
			}
		},
		buildParentCategories() {
			const incomeCategories = this.classifications.filter(c => c.mgClassification.grandParentClassification === GRANDPARENT_CLASSIFICATIONS.INCOME);
			const futureCategories = this.classifications.filter(c => c.mgClassification.grandParentClassification === GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING);
			const everydayCategories = this.classifications.filter(c => c.mgClassification.grandParentClassification === GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND);
			const committedCategories = this.classifications.filter(c => c.mgClassification.grandParentClassification === GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS);

			this.parentCategories = [
				{ name: 'Income', categories: incomeCategories, colour: '#B4D8D7' },
				{ name: 'Future Plans', categories: futureCategories, colour: '#D5C9C8' },
				{ name: 'Everyday', categories: everydayCategories, colour: '#9EA2D0' },
				{ name: 'Committed', categories: committedCategories, colour: '#413655' }
			];
		},
		selectParentFromCurrentSelection() {
			if (!this.selectedClassificationId) {
				// Default to first parent
				if (this.parentCategories.length > 0) {
					this.selectedParent = this.parentCategories[0];
				}
				return;
			}

			// Find which parent contains the current selection
			for (const parent of this.parentCategories) {
				for (const cat of parent.categories) {
					if (cat.mgClassification.id === this.selectedClassificationId) {
						this.selectedParent = parent;
						this.selectedClassificationObj = cat;
						return;
					}
					for (const child of cat.children || []) {
						if (child.mgClassification.id === this.selectedClassificationId) {
							this.selectedParent = parent;
							this.selectedClassificationObj = child;
							return;
						}
					}
				}
			}

			// Default to first parent if not found
			if (this.parentCategories.length > 0) {
				this.selectedParent = this.parentCategories[0];
			}
		},
		selectParent(parent) {
			this.selectedParent = parent;
		},
		selectCategory(category) {
			this.selectedClassificationId = category.mgClassification.id;
			this.selectedClassificationObj = category;
			this.$nextTick(() => {
				const actions = this.$refs.modal?.querySelector('.modal-actions');
				if (actions) {
					actions.scrollIntoView({ behavior: 'smooth', block: 'end' });
				}
			});
		},
		applySuggestion() {
			if (!this.suggestedClassification) return;

			// Find the classification object
			const found = this.findClassificationById(this.suggestedClassification.id);
			if (found) {
				this.selectedClassificationId = found.mgClassification.id;
				this.selectedClassificationObj = found;

				// Select the appropriate parent
				this.selectParentFromCurrentSelection();

				// Scroll to selected item after DOM updates, then scroll modal to bottom
				this.$nextTick(() => {
					this.scrollToSelectedSubcategory();
					const actions = this.$refs.modal?.querySelector('.modal-actions');
					if (actions) {
						actions.scrollIntoView({ behavior: 'smooth', block: 'end' });
					}
				});
			}
		},
		scrollToSelectedSubcategory() {
			if (!this.$refs.subcategoryList || !this.selectedClassificationId) return;
			const selectedEl = this.$refs.subcategoryList.querySelector(`[data-id="${this.selectedClassificationId}"]`);
			if (selectedEl) {
				selectedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		},
		findClassificationById(id) {
			for (const cat of this.classifications) {
				if (cat.mgClassification.id === id) return cat;
				for (const child of cat.children || []) {
					if (child.mgClassification.id === id) return child;
				}
			}
			return null;
		},
		confirmTag() {
			if (!this.selectedClassificationObj || !this.localTransaction?.id || this.saving) return;
			this.saving = true;

			const updatedTransaction = {
				id: this.localTransaction.id,
				bankAccountID: this.localTransaction.bankAccountID,
				merchantName: this.localTransaction.merchantName,
				amount: this.localTransaction.amount,
				customerClassification: this.selectedClassificationObj.mgClassification,
				effectiveClassification: this.selectedClassificationObj.mgClassification,
				excludeFromAnalysis: this.excludeFromAnalysis,
				alwaysApplyTag: this.alwaysApplyTag
			};

			const onSuccess = () => {
				this.saving = false;
				this.$emit('tagged', updatedTransaction);
				this.showConfirmTick = true;
				setTimeout(() => {
					this.showConfirmTick = false;
					if (this.bulkMode) {
						// Reset state for next transaction
						this.resetState();
						this.$emit('next');
					} else {
						this.$emit('close');
					}
				}, 800);
			};

			if (this.alwaysApplyTag) {
				// Create merchant rule
				const payload = {
					alwaysExcludeFromAnalysis: this.excludeFromAnalysis,
					merchantName: this.localTransaction.merchantName,
					chosenTransactionClassification: {
						mgClassification: this.selectedClassificationObj.mgClassification.mgClassification,
						mgSubClassification: this.selectedClassificationObj.mgClassification.mgSubClassification,
						grandParentClassification: this.selectedClassificationObj.mgClassification.grandParentClassification
					}
				};
				this._updateTransactionClassificationMerchantRule(payload, this.bulkMode ? () => {} : onSuccess);
			} else {
				// Update single transaction
				this._updateTransactionClassification(updatedTransaction, this.bulkMode ? () => {} : onSuccess);
			}

			// In bulk mode, proceed immediately without waiting for API
			if (this.bulkMode) {
				onSuccess();
			}
		},
		getParentStyle(parent) {
			if (this.selectedParent?.name === parent.name) {
				return {
					backgroundColor: parent.colour,
					color: parent.colour === '#413655' ? 'white' : '#1a1a1a'
				};
			}
			return {};
		},
		formatDate(timestamp) {
			const date = new Date(timestamp);
			let result = date.toLocaleDateString('en-GB', {
				day: 'numeric',
				month: 'short',
				year: 'numeric'
			});
			if (date.getHours() !== 0 || date.getMinutes() !== 0) {
				result += ' · ' + date.toLocaleTimeString('en-GB', {
					hour: '2-digit',
					minute: '2-digit'
				});
			}
			return result;
		},
		formatAmount(amount) {
			if (!amount) return '£0.00';
			const formatted = new Intl.NumberFormat('en-GB', {
				style: 'currency',
				currency: 'GBP'
			}).format(Math.abs(amount));
			return amount < 0 ? `-${formatted}` : formatted;
		},
		resetState() {
			this.searchQuery = '';
			this.suggestedClassification = null;
			this.loadingSuggestion = false;
			this.selectedParent = null;
			this.selectedClassificationId = null;
			this.selectedClassificationObj = null;
			this.alwaysApplyTag = true;
			this.excludeFromAnalysis = false;
			this.localTransaction = null;
			this.availableTags = null;
			this.selectedTags = [];
			this.saving = false;
		},
		handleTagsChange(tags) {
			const updatedTags = tags.map(newTag => {
				const existingTag = this.localTransaction.customerTags?.find(t => t.tag === (newTag.tag || newTag));
				if (existingTag) {
					return { tag: existingTag.tag, id: existingTag.id };
				}
				return { tag: newTag.tag || newTag };
			});

			this._updateTransactionTags(this.localTransaction.bankAccountID, this.localTransaction.id, updatedTags, () => {
				this._clearCustomerTagsCache();
				// Update local transaction tags
				this.localTransaction.customerTags = updatedTags;
				this.selectedTags = tags;
				// Notify parent to update transaction in list
				this.$emit('tagged', {
					...this.localTransaction,
					customerTags: updatedTags
				});
			});
		},
		handleNewTag() {
			// Tag was added - nothing extra needed, handleTagsChange handles the API call
		},
		onDialogUpdate(value) {
			// Only emit close when dialog is actually closing (not on other updates)
			if (!value) {
				this.$emit('close');
			}
		}
	}
};
</script>

<style scoped>
.tagging-modal {
	background: var(--light-bg-primary, white);
	border-radius: 12px;
	overflow-y: auto;
	max-height: 90vh;
	position: relative;
}

/* Success Tick Overlay */
.confirm-tick-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(255, 255, 255, 0.9);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 100;
	border-radius: 12px;
}

.tick-fade-enter-active {
	transition: opacity 0.2s ease-out;
}

.tick-fade-leave-active {
	transition: opacity 0.4s ease-in;
}

.tick-fade-enter-from,
.tick-fade-leave-to {
	opacity: 0;
}

/* Header */
.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px 24px;
	border-bottom: 1px solid var(--light-border, #f0f0f0);
}

.header-left {
	display: flex;
	align-items: center;
	gap: 12px;
}

.modal-title {
	font-size: 18px;
	margin: 0;
	color: var(--light-text-primary, #1a1a1a);
}

.progress-indicator {
	font-size: 13px;
	color: var(--light-text-secondary, #666);
	background: var(--light-bg-secondary, #f8f8f8);
	padding: 4px 10px;
	border-radius: 12px;
}

.close-btn {
	background: none;
	border: none;
	cursor: pointer;
	padding: 4px;
	color: var(--light-text-secondary, #666);
	border-radius: 4px;
}

.close-btn:hover {
	background: var(--light-border, #f0f0f0);
}

/* Transaction Info */
.transaction-info {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 24px;
	background: var(--light-bg-secondary, #f8f8f8);
}

.transaction-info-left {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.merchant-name {
	font-size: 15px;
	color: var(--light-text-primary, #1a1a1a);
}

.transaction-date {
	font-size: 12px;
	color: var(--light-text-secondary, #666);
}

.transaction-info-right {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 2px;
}

.transaction-amount {
	font-size: 15px;
	color: var(--light-text-primary, #1a1a1a);
}

.merchant-count {
	font-size: 12px;
	color: var(--light-text-secondary, #666);
}

/* Search Section */
.search-section {
	padding: 16px 24px;
}

/* Suggestion Banner */
.suggestion-banner {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 24px;
	background: #f0edf5;
	border-top: 1px solid #d4cce0;
	border-bottom: 1px solid #d4cce0;
}

.suggestion-banner.loading {
	justify-content: flex-start;
	gap: 12px;
}

.suggestion-content {
	display: flex;
	align-items: center;
	gap: 8px;
}

.suggestion-text {
	font-size: 14px;
	color: var(--light-text-primary, #1a1a1a);
}

.apply-suggestion-btn {
	background: #413655;
	border: none;
	padding: 6px 16px;
	border-radius: 6px;
	font-size: 13px;
	cursor: pointer;
	color: white;
}

.apply-suggestion-btn:hover {
	background: #5a4a70;
}

/* Category Panels */
.category-panels {
	display: flex;
	height: 220px;
	border-top: 1px solid var(--light-border, #f0f0f0);
	border-bottom: 1px solid var(--light-border, #f0f0f0);
}

.parent-panel {
	width: 180px;
	border-right: 1px solid var(--light-border, #f0f0f0);
	display: flex;
	flex-direction: column;
}

.subcategory-panel {
	flex: 1;
	display: flex;
	flex-direction: column;
}

.panel-header {
	padding: 12px 16px;
	font-size: 12px;
	text-transform: uppercase;
	color: var(--light-text-secondary, #666);
	border-bottom: 1px solid var(--light-border, #f0f0f0);
	background: var(--light-bg-tertiary, #fafafa);
}

.parent-list,
.subcategory-list {
	flex: 1;
	overflow-y: auto;
}

.parent-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding: 12px 16px;
	border: none;
	background: none;
	cursor: pointer;
	text-align: left;
	font-size: 14px;
	color: var(--light-text-primary, #1a1a1a);
	transition: background 0.15s;
}

.parent-item:hover {
	background: var(--light-bg-hover, #f5f5f5);
}

.parent-item.selected {
	background: var(--brand-purple, #413655);
	color: white;
}

.subcategory-item {
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	padding: 12px 16px;
	border: none;
	background: none;
	cursor: pointer;
	text-align: left;
	font-size: 14px;
	color: var(--light-text-primary, #1a1a1a);
	transition: background 0.15s;
}

.subcategory-item.child {
	padding-left: 32px;
}

.subcategory-item:hover {
	background: var(--light-bg-hover, #f5f5f5);
}

.subcategory-item.selected {
	background: var(--light-border, #f0f0f0);
}

.radio-dot {
	width: 16px;
	height: 16px;
	border: 2px solid var(--light-border-strong, #ddd);
	border-radius: 50%;
	flex-shrink: 0;
}

.radio-dot.checked {
	border-color: var(--brand-purple, #413655);
	background: var(--brand-purple, #413655);
	box-shadow: inset 0 0 0 3px white;
}

.empty-state {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	color: var(--light-text-muted, #999);
	font-size: 14px;
}

/* Toggle Section */
.toggle-section {
	padding: 8px 24px;
	background: var(--light-bg-secondary, #f8f8f8);
}

.toggle-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin: 8px 0;
}

.toggle-label {
	font-size: 14px;
	color: var(--light-text-primary, #1a1a1a);
}

/* Tags Section */
.tags-section {
	padding: 16px 24px;
}

.tags-label {
	font-size: 14px;
	color: var(--light-text-primary, #1a1a1a);
	margin-bottom: 8px;
}

.tags-section :deep(.tag-input) {
	background: var(--light-bg-primary, white);
	border-color: var(--light-border-strong, #ddd);
}

.tags-section :deep(.tag-menu) {
	z-index: 9999;
}

.tags-section :deep(.v-field) {
	background: transparent !important;
}

/* Actions */
.modal-actions {
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	padding: 16px 24px;
	border-top: 1px solid var(--light-border, #f0f0f0);
}

.cancel-btn,
.skip-btn,
.confirm-btn {
	padding: 10px 24px;
	border-radius: 8px;
	font-size: 14px;
	cursor: pointer;
	transition: all 0.15s;
}

.cancel-btn {
	background: var(--light-bg-primary, white);
	border: 1px solid var(--light-border-strong, #ddd);
	color: var(--light-text-secondary, #666);
}

.cancel-btn:hover {
	background: var(--light-bg-hover, #f5f5f5);
}

.skip-btn {
	background: var(--light-bg-primary, white);
	border: 1px solid var(--light-border-strong, #ddd);
	color: var(--light-text-secondary, #666);
}

.skip-btn:hover {
	background: var(--light-bg-hover, #f5f5f5);
}

.confirm-btn {
	background: var(--brand-purple, #413655);
	border: none;
	color: white;
}

.confirm-btn:hover:not(:disabled) {
	background: var(--brand-purple-light, #5a4a70);
}

.confirm-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}
</style>

<!-- Dark Mode Styles -->
<style>
.tagging-modal.dark {
	background: var(--dark-bg-secondary);
}

.tagging-modal.dark .modal-header {
	border-color: var(--dark-border);
}

.tagging-modal.dark .modal-title {
	color: var(--dark-text-primary);
}

.tagging-modal.dark .progress-indicator {
	color: var(--dark-text-secondary);
	background: var(--dark-bg-primary);
}

.tagging-modal.dark .close-btn {
	color: var(--dark-text-secondary);
}

.tagging-modal.dark .close-btn:hover {
	background: var(--dark-bg-hover);
}

.tagging-modal.dark .transaction-info {
	background: var(--dark-bg-primary);
}

.tagging-modal.dark .merchant-name,
.tagging-modal.dark .transaction-amount {
	color: var(--dark-text-primary);
}

.tagging-modal.dark .transaction-date,
.tagging-modal.dark .merchant-count {
	color: var(--dark-text-secondary);
}

.tagging-modal.dark .search-section .v-field {
	background: var(--dark-bg-primary) !important;
}

.tagging-modal.dark .search-section input {
	color: var(--dark-text-primary) !important;
}

.tagging-modal.dark .suggestion-banner {
	background: rgba(65, 54, 85, 0.3);
	border-color: #5a4a70;
}

.tagging-modal.dark .suggestion-text {
	color: var(--dark-text-primary);
}

.tagging-modal.dark .category-panels {
	border-color: var(--dark-border);
}

.tagging-modal.dark .parent-panel {
	border-color: var(--dark-border);
}

.tagging-modal.dark .panel-header {
	background: var(--dark-bg-primary);
	border-color: var(--dark-border);
	color: var(--dark-text-secondary);
}

.tagging-modal.dark .parent-item {
	color: var(--dark-text-primary);
}

.tagging-modal.dark .parent-item:hover {
	background: var(--dark-bg-hover);
}

.tagging-modal.dark .subcategory-item {
	color: var(--dark-text-primary);
}

.tagging-modal.dark .subcategory-item:hover {
	background: var(--dark-bg-hover);
}

.tagging-modal.dark .subcategory-item.selected {
	background: var(--dark-bg-hover);
}

.tagging-modal.dark .radio-dot {
	border-color: var(--dark-text-secondary);
}

.tagging-modal.dark .empty-state {
	color: var(--dark-text-secondary);
}

.tagging-modal.dark .toggle-section {
	background: var(--dark-bg-primary);
}

.tagging-modal.dark .tags-label {
	color: var(--dark-text-primary);
}

.tagging-modal.dark .tag-input {
	background: var(--dark-bg-primary);
	border-color: var(--dark-border);
}

.tagging-modal.dark .tag-input input {
	color: var(--dark-text-primary) !important;
}

.tagging-modal.dark .tag-menu {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-border);
}

.tagging-modal.dark .tag-menu-item:hover {
	background: var(--dark-bg-hover);
}

.tagging-modal.dark .toggle-label {
	color: var(--dark-text-primary);
}

.tagging-modal.dark .modal-actions {
	border-color: var(--dark-border);
}

.tagging-modal.dark .cancel-btn,
.tagging-modal.dark .skip-btn {
	background: var(--dark-bg-primary);
	border-color: var(--dark-border);
	color: var(--dark-text-primary);
}

.tagging-modal.dark .cancel-btn:hover,
.tagging-modal.dark .skip-btn:hover {
	background: var(--dark-bg-hover);
}

.tagging-modal.dark .confirm-tick-overlay {
	background: rgba(30, 30, 30, 0.9);
}
</style>
